/**
 * DynamicProductTile — one SCAPI-backed product tile whose parts an author
 * toggles in Builder. `standard` composes `HecProductTileCms`; `feature` is the
 * CM Enhanced Content card. LMN / telehealth products always get the standard
 * tile, which dispatches them to their own clones (no price / ATC for LMN —
 * SFCC-11677; intake CTA for telehealth — ET-225). Quick View is wired the way
 * `RecsCarouselTile` does it, so a master's Add to Cart opens the variant
 * picker instead of being suppressed.
 */
import { useState } from "react";
import { useIntl } from "react-intl";

import type { ProductHitWithSwatches } from "~/shop/products/productSearch.server";

import {
  ProductQuickView,
  ProductQuickViewDialog,
} from "~/components/products/product-quick-view";
import { HecProductTileCms } from "~/hec/components/product/product-tile";
import { ProductTileSkeleton } from "~/hec/components/product/product-tile-skeleton";
import { resolveMasterIdForQuickView } from "~/hec/components/recs/recs-master-id";
import { useDynamicProductTile } from "~/hec/dynamic-product-tile/use-dynamic-product-tile";
import { isLMNProduct, isTelehealthProduct } from "~/hec/types/product";
import { cn } from "~/lib/utils";

import { DEFAULT_TILE_DISPLAY } from "./display-defaults";
import { FeatureProductCard } from "./feature-product-card";
import { FeatureProductCardSkeleton } from "./feature-product-card-skeleton";
import messages from "./messages";
import { sharedTileOverrides } from "./shared-tile-overrides";
import type { DynamicProductTileProps, SharedTileKind } from "./types";

const ALIGN_CLASS = {
  left: "mr-auto",
  center: "mx-auto",
  right: "ml-auto",
} as const;

const EDITOR_MESSAGE = {
  idle: "Enter a SKU to preview the tile.",
  "not-found":
    "No product found for this SKU — nothing renders on the live storefront.",
  error:
    "The product couldn't be loaded — nothing renders on the live storefront.",
} as const;

export function DynamicProductTile({
  productId,
  product: preloaded,
  layout = DEFAULT_TILE_DISPLAY.layout,
  greatFor,
  keyBenefits,
  imageSrc,
  showBadges = DEFAULT_TILE_DISPLAY.showBadges,
  showPrice = DEFAULT_TILE_DISPLAY.showPrice,
  showRatings = DEFAULT_TILE_DISPLAY.showRatings,
  showAddToCart = DEFAULT_TILE_DISPLAY.showAddToCart,
  showFavorites = DEFAULT_TILE_DISPLAY.showFavorites,
  showMessaging = DEFAULT_TILE_DISPLAY.showMessaging,
  showQuickView = DEFAULT_TILE_DISPLAY.showQuickView,
  borderColor,
  maxWidth,
  align = "center",
  listId,
  listName,
  listIndex = 1,
  inEditor = false,
  className,
  style,
  ...rest
}: DynamicProductTileProps) {
  const intl = useIntl();
  // A parent that already loaded the product (FeaturedProductSlider) passes
  // it in; `null` keeps this tile's own read idle.
  const fetched = useDynamicProductTile(preloaded ? null : productId);
  const status = preloaded ? "ready" : fetched.status;
  const product = preloaded ?? fetched.product;
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  // Tile stepper qty at QV open — pre-fills the buy form (SFCC-11825).
  const [quickViewQty, setQuickViewQty] = useState<number | undefined>();

  const openQuickView = showQuickView
    ? (quantity?: number) => {
        setQuickViewQty(quantity);
        setQuickViewOpen(true);
      }
    : undefined;
  const gtmListId = listId || productId || "";
  const gtmListName = listName || gtmListId;

  const rootClass = cn("w-full", ALIGN_CLASS[align], className);
  // Merge, don't let a spread `style` replace the max width.
  const rootStyle = {
    ...(maxWidth ? { maxWidth: `${maxWidth}px` } : {}),
    ...style,
  };
  const sharedKind: SharedTileKind | null = !product
    ? null
    : isLMNProduct(product)
      ? "lmn"
      : isTelehealthProduct(product)
        ? "telehealth"
        : "standard";
  const useFeatureCard = layout === "feature" && sharedKind === "standard";

  if (status === "idle" || status === "not-found" || status === "error") {
    if (!inEditor) return null;
    return (
      <div
        className={rootClass}
        style={rootStyle}
        data-testid="product-tile-dynamic-empty"
        {...rest}
      >
        <div className="flex min-h-[240px] flex-col items-center justify-center gap-1 border-2 border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500">
          <span className="font-semibold text-gray-700">
            Dynamic Product Tile
          </span>
          {productId && <span>Product: {productId}</span>}
          <span>{EDITOR_MESSAGE[status]}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={rootClass}
      style={rootStyle}
      data-testid={`product-tile-dynamic-root-${productId ?? ""}`}
      {...rest}
    >
      {status === "loading" || !product ? (
        layout === "feature" ? (
          <FeatureProductCardSkeleton />
        ) : (
          <div className="bg-white">
            <ProductTileSkeleton />
          </div>
        )
      ) : (
        <>
          {useFeatureCard ? (
            <FeatureProductCard
              product={product}
              features={[
                {
                  label: intl.formatMessage(messages.greatFor),
                  text: greatFor,
                },
                {
                  label: intl.formatMessage(messages.keyBenefits),
                  text: keyBenefits,
                },
              ]}
              imageSrc={imageSrc}
              showBadges={showBadges}
              showPrice={showPrice}
              showRatings={showRatings}
              showAddToCart={showAddToCart}
              showFavorites={showFavorites}
              onOpenQuickView={openQuickView}
              borderColor={borderColor}
              listId={gtmListId}
              listName={gtmListName}
              listIndex={listIndex}
            />
          ) : (
            // Badge / price toggles and row alignment are applied from this
            // wrapper — the shared tiles stay untouched (see
            // `shared-tile-overrides.ts` for the coupling this takes on).
            <div
              className={cn(
                "h-full",
                sharedTileOverrides({
                  kind: sharedKind ?? "standard",
                  showBadges,
                  showPrice,
                }),
              )}
              data-testid={`product-tile-dynamic-shared-root-${product.id}`}
            >
              <HecProductTileCms
                product={product}
                enableRatings={showRatings}
                enableAddToCart={showAddToCart}
                enableFavorites={showFavorites}
                enableMessaging={showMessaging}
                onOpenQuickView={openQuickView}
                className={cn("h-full", borderColor && "border-2 border-solid")}
                style={borderColor ? { borderColor } : undefined}
                gtmListId={gtmListId}
                gtmListName={gtmListName}
                gtmIndex={listIndex}
              />
            </div>
          )}
          {showQuickView && (
            <ProductQuickViewDialog
              open={quickViewOpen}
              onClose={() => setQuickViewOpen(false)}
            >
              <ProductQuickView
                product={
                  {
                    productId: resolveMasterIdForQuickView(product),
                  } as ProductHitWithSwatches
                }
                initialQuantity={quickViewQty}
                onClose={() => setQuickViewOpen(false)}
              />
            </ProductQuickViewDialog>
          )}
        </>
      )}
    </div>
  );
}
